import { GridColDef, GridRowId } from '@mui/x-data-grid-pro';
import { GridValidRowModel } from '@mui/x-data-grid/models/gridRows';
import { useCallback, useMemo, useState } from 'react';

import { TTier } from '@modules/admin/Tools/LenderUtility/interfaces';
import { lenderUtilityTiersValidationSchema } from '@modules/admin/Tools/LenderUtility/validation';
import { gridDateTimeFormatter } from '@shared/commonFunctions';
import { dataGridCol } from '@shared/components/grid/common';
import { GridColNumber } from '@shared/components/grid/GridColNumber';
import { GridColTextInput } from '@shared/components/grid/GridColTextInput';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';

import { AlertModal } from '@shared/components/modals/AlertModal';
import { requestCopyTier, requestDeleteTier, requestUpsertTiers } from './queries';

const columns: GridColDef[] = [
  dataGridCol('tierLabel', {
    editable: true,
    renderEditCell: (params) => <GridColTextInput params={params} />,
  }),
  dataGridCol('tierDescription', {
    editable: true,
    renderEditCell: (params) => <GridColTextInput params={params} />,
  }),
  dataGridCol('minFico', {
    editable: true,
    renderEditCell: (params) => <GridColNumber params={params} />,
  }),
  dataGridCol('maxFico', {
    editable: true,
    renderEditCell: (params) => <GridColNumber params={params} />,
  }),
  dataGridCol('updatedBy', {
    headerName: 'Updated By',
    valueGetter: ({ row }) => row.updatedBy?.name ?? '',
  }),
  dataGridCol('updatedDate', {
    headerName: 'Updated Date',
    type: 'dateTime',
    valueFormatter: gridDateTimeFormatter,
  }),
].map((column) => ({ ...column, flex: 1, minWidth: 120 }));

interface ITiersProps {
  data: TTier[];
  cardId?: string;
  lenderId: string;
  selectedRowId?: GridRowId;
  onSelectRow(row: TTier): void;
  mutate?(): void;
}

export const Tiers = ({ data = [], lenderId, cardId, selectedRowId, onSelectRow, mutate }: ITiersProps) => {
  const [showAlert, setShowAlert] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleAlertConfirm = useCallback(async () => {
    setLoading(true);
    await requestCopyTier(lenderId, showAlert ?? '');
    setLoading(false);
    setShowAlert(null);
  }, [lenderId, showAlert]);

  const gridActions = useMemo(
    () => ({
      onSave: async (row: GridValidRowModel) => {
        const response = await requestUpsertTiers(lenderId, cardId, row);

        if (!response?.error) {
          mutate?.();
        }

        return response;
      },
      onCopy: (row: GridValidRowModel) => setShowAlert(row?.id),
      onDelete: async (row: GridValidRowModel) => await requestDeleteTier(lenderId, row?.id),
    }),
    [lenderId, cardId]
  );

  const onRowClick = useCallback(({ row }: { row: TTier }, event: React.MouseEvent) => {
    event.stopPropagation();
    onSelectRow(row);
  }, []);

  return useMemo(
    () => (
      <>
        {!!showAlert && (
          <AlertModal
            title={'Alert'}
            subtitle='Copy Selected Tier & Conditions?'
            open={!!showAlert}
            buttonText={'Ok'}
            isLoading={loading}
            onConfirm={handleAlertConfirm}
            onClose={() => setShowAlert(null)}
          />
        )}
        <PhoenixGrid
          autoHeight
          withHeader
          name="tier"
          pageSize={10}
          rowsPerPageOptions={[10]}
          columns={columns}
          loading={!data}
          rows={data || []}
          validateSchema={lenderUtilityTiersValidationSchema}
          selectionModel={selectedRowId && [selectedRowId]}
          gridId={`tier-${lenderId}`}
          actions={gridActions}
          disabled={!cardId}
          initialState={{
            sorting: {
              sortModel: [{ field: 'minFico', sort: 'desc' }],
            },
          }}
          onRowClick={onRowClick}
        />
      </>
    ),
    [data, selectedRowId, gridActions, cardId, showAlert, handleAlertConfirm, loading]
  );
};
